import type { ArmorItem, BootItem, HelmetItem, Material, ShieldItem, WeaponItem } from "../types.ts";
import { ellipse, INK, paint, shade, type Ctx } from "./util.ts";

export const MATERIAL: Record<Material, string> = {
  wood: "#9a6434",
  corn: "#f2c94c",
  rusty: "#a8643c",
  bronze: "#cd8f3c",
  iron: "#a3acb6",
  steel: "#dbe3ea",
  gold: "#f5c431",
  egg: "#fff6dc",
  cosmic: "#8f5cf0",
  leather: "#8a5a2b",
  cloth: "#d9c7a0",
};

function metal(ctx: Ctx, m: Material, x0: number, y0: number, x1: number, y1: number): CanvasGradient {
  const base = MATERIAL[m];
  const g = ctx.createLinearGradient(x0, y0, x1, y1);
  g.addColorStop(0, shade(base, 0.45));
  g.addColorStop(0.5, base);
  g.addColorStop(1, shade(base, -0.3));
  return g;
}

function glow(ctx: Ctx, m: Material, time: number): void {
  if (m !== "egg" && m !== "cosmic" && m !== "gold") return;
  ctx.shadowColor = m === "cosmic" ? "#c79bff" : "#ffe58a";
  ctx.shadowBlur = m === "gold" ? 6 : 14 + Math.sin(time * 4) * 5;
}

const HANDLE = "#6b4220";

function handle(ctx: Ctx, from: number, to: number, w = 5, color = HANDLE): void {
  ctx.beginPath();
  ctx.roundRect(from, -w / 2, to - from, w, w / 2);
  paint(ctx, color, 2.5);
}

/** Weapon drawn along +x, grip at the origin. */
export function drawWeapon(ctx: Ctx, w: WeaponItem, time: number): void {
  const m = w.material;
  const c = MATERIAL[m];
  ctx.save();
  switch (w.shape) {
    case "stick": {
      ctx.beginPath();
      ctx.moveTo(-10, -2.5);
      ctx.lineTo(44, -1);
      ctx.lineTo(50, 0);
      ctx.lineTo(44, 1.5);
      ctx.lineTo(-10, 3);
      ctx.closePath();
      paint(ctx, c, 2.5);
      ctx.beginPath();
      ctx.moveTo(22, -1);
      ctx.lineTo(30, -9);
      ctx.lineWidth = 3;
      ctx.strokeStyle = INK;
      ctx.stroke();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = c;
      ctx.stroke();
      break;
    }
    case "club": {
      handle(ctx, -10, 16, 6, "#7a8f3a");
      ellipse(ctx, 34, 0, 22, 9);
      paint(ctx, c);
      ctx.fillStyle = shade(c, -0.25);
      for (let i = 0; i < 6; i++) for (let j = -1; j <= 1; j++) {
        ctx.beginPath();
        ctx.arc(18 + i * 6.5, j * 4.5, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.beginPath();
      ctx.moveTo(14, -3);
      ctx.quadraticCurveTo(26, -16, 40, -10);
      ctx.quadraticCurveTo(26, -8, 16, 0);
      ctx.moveTo(14, 3);
      ctx.quadraticCurveTo(26, 16, 40, 10);
      ctx.quadraticCurveTo(26, 8, 16, 0);
      paint(ctx, "#8fae45", 2);
      break;
    }
    case "knife": {
      handle(ctx, -10, 10, 6, "#e8e0cf");
      ctx.beginPath();
      ctx.moveTo(10, -3.5);
      ctx.lineTo(34, -3.5);
      ctx.quadraticCurveTo(42, -3, 40, 2);
      ctx.lineTo(10, 3);
      ctx.closePath();
      paint(ctx, metal(ctx, m, 0, -4, 0, 4), 2.5);
      break;
    }
    case "spork": {
      handle(ctx, -10, 26, 5, c);
      ellipse(ctx, 34, 0, 9, 7);
      paint(ctx, c, 2.5);
      ctx.strokeStyle = INK;
      ctx.lineWidth = 2;
      for (const y of [-3.5, 0, 3.5]) {
        ctx.beginPath();
        ctx.moveTo(38, y);
        ctx.lineTo(44, y);
        ctx.stroke();
      }
      break;
    }
    case "sword": {
      glow(ctx, m, time);
      ctx.beginPath();
      ctx.moveTo(10, -4.5);
      ctx.lineTo(56, -3);
      ctx.lineTo(66, 0);
      ctx.lineTo(56, 3);
      ctx.lineTo(10, 4.5);
      ctx.closePath();
      paint(ctx, metal(ctx, m, 0, -5, 0, 5), 2.5);
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.moveTo(14, 0);
      ctx.lineTo(54, 0);
      ctx.strokeStyle = shade(MATERIAL[m], -0.2);
      ctx.lineWidth = 1.2;
      ctx.stroke();
      handle(ctx, -10, 8, 5);
      ctx.beginPath();
      ctx.roundRect(6, -11, 6, 22, 2);
      paint(ctx, m === "egg" ? "#f5c431" : shade(MATERIAL[m], -0.1), 2.5);
      ellipse(ctx, -11, 0, 4, 4);
      paint(ctx, m === "egg" ? "#f5c431" : MATERIAL[m], 2);
      if (m === "egg") {
        ellipse(ctx, 9, 0, 3.4, 4.2);
        paint(ctx, "#ff9d2e", 1.5);
      }
      break;
    }
    case "axe": {
      const big = w.twoHanded;
      const len = big ? 64 : 48;
      handle(ctx, -12, len, big ? 6 : 5);
      ctx.beginPath();
      const hx = len - 14;
      ctx.moveTo(hx, -3);
      ctx.lineTo(hx - 4, big ? -24 : -17);
      ctx.quadraticCurveTo(hx + 14, big ? -30 : -21, hx + 20, big ? -18 : -12);
      ctx.quadraticCurveTo(hx + 10, -6, hx + 10, -3);
      ctx.closePath();
      paint(ctx, metal(ctx, m, hx, -24, hx + 20, 0));
      if (big) {
        ctx.beginPath();
        ctx.moveTo(hx, 3);
        ctx.lineTo(hx - 4, 24);
        ctx.quadraticCurveTo(hx + 14, 30, hx + 20, 18);
        ctx.quadraticCurveTo(hx + 10, 6, hx + 10, 3);
        ctx.closePath();
        paint(ctx, metal(ctx, m, hx, 0, hx + 20, 24));
      }
      break;
    }
    case "mace": {
      handle(ctx, -10, 40, 5);
      ctx.fillStyle = shade(c, -0.2);
      ctx.strokeStyle = INK;
      ctx.lineWidth = 2;
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2;
        ctx.beginPath();
        ctx.moveTo(48 + Math.cos(a - 0.25) * 10, Math.sin(a - 0.25) * 10);
        ctx.lineTo(48 + Math.cos(a) * 18, Math.sin(a) * 18);
        ctx.lineTo(48 + Math.cos(a + 0.25) * 10, Math.sin(a + 0.25) * 10);
        ctx.fill();
        ctx.stroke();
      }
      ellipse(ctx, 48, 0, 11, 11);
      paint(ctx, metal(ctx, m, 40, -11, 56, 11));
      break;
    }
    case "spear": {
      handle(ctx, -40, 76, 5);
      ctx.beginPath();
      ctx.moveTo(74, -6);
      ctx.quadraticCurveTo(88, -6, 100, 0);
      ctx.quadraticCurveTo(88, 6, 74, 6);
      ctx.closePath();
      paint(ctx, metal(ctx, m, 74, -6, 74, 6));
      ctx.beginPath();
      ctx.moveTo(70, -5);
      ctx.lineTo(64, -12);
      ctx.moveTo(70, 5);
      ctx.lineTo(64, 12);
      ctx.strokeStyle = "#c62828";
      ctx.lineWidth = 3;
      ctx.stroke();
      break;
    }
    case "fork": {
      handle(ctx, -40, 70, 5, w.material === "bronze" ? shade(c, -0.2) : HANDLE);
      ctx.beginPath();
      ctx.roundRect(66, -13, 6, 26, 2);
      paint(ctx, metal(ctx, m, 66, -13, 72, 13), 2.5);
      for (let i = 0; i < 3; i++) {
        const y = -10 + i * 10;
        ctx.beginPath();
        ctx.moveTo(70, y - 2);
        ctx.lineTo(92, y - 1.5);
        ctx.lineTo(w.material === "bronze" ? 100 : 96, y);
        ctx.lineTo(92, y + 1.5);
        ctx.lineTo(70, y + 2);
        ctx.closePath();
        paint(ctx, metal(ctx, m, 70, y - 2, 70, y + 2), 2);
      }
      break;
    }
    case "cleaver": {
      handle(ctx, -10, 14, 6);
      ctx.beginPath();
      ctx.moveTo(12, -6);
      ctx.lineTo(46, -6);
      ctx.lineTo(48, 18);
      ctx.lineTo(14, 18);
      ctx.closePath();
      paint(ctx, metal(ctx, m, 12, -6, 48, 18));
      ellipse(ctx, 40, -1, 2.5, 2.5);
      paint(ctx, INK, 0);
      break;
    }
    case "hammer": {
      handle(ctx, -24, 66, 6);
      ctx.beginPath();
      ctx.roundRect(56, -20, 18, 40, 3);
      paint(ctx, metal(ctx, m, 56, -20, 74, 20));
      ctx.beginPath();
      ctx.moveTo(56, -14);
      ctx.lineTo(74, -14);
      ctx.moveTo(56, 14);
      ctx.lineTo(74, 14);
      ctx.strokeStyle = INK;
      ctx.lineWidth = 2;
      ctx.stroke();
      break;
    }
    case "scythe": {
      handle(ctx, -40, 70, 5);
      glow(ctx, m, time);
      ctx.beginPath();
      ctx.moveTo(66, -2);
      ctx.quadraticCurveTo(70, -40, 30, -54);
      ctx.quadraticCurveTo(56, -32, 58, -2);
      ctx.closePath();
      paint(ctx, metal(ctx, m, 30, -54, 70, 0));
      break;
    }
    case "drumstick": {
      glow(ctx, m, time);
      ctx.beginPath();
      ctx.roundRect(-14, -5, 34, 10, 5);
      paint(ctx, "#f6efe0", 2.5);
      ellipse(ctx, -14, -5, 5, 5);
      paint(ctx, "#f6efe0", 2.5);
      ellipse(ctx, -14, 5, 5, 5);
      paint(ctx, "#f6efe0", 2.5);
      ctx.beginPath();
      ctx.moveTo(16, -8);
      ctx.bezierCurveTo(30, -26, 76, -26, 78, 0);
      ctx.bezierCurveTo(76, 26, 30, 26, 16, 8);
      ctx.closePath();
      const g = ctx.createRadialGradient(54, -8, 4, 50, 0, 30);
      g.addColorStop(0, "#f2b45a");
      g.addColorStop(1, "#a85a1e");
      paint(ctx, g);
      ctx.shadowBlur = 0;
      ctx.fillStyle = "rgba(255,240,200,0.6)";
      for (const [x, y] of [[40, -8], [56, 6], [62, -10], [34, 6]] as const) {
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
  }
  ctx.restore();
}

/** Shield centered at the origin, seen mostly face-on. */
export function drawShield(ctx: Ctx, s: ShieldItem, time: number): void {
  const c = MATERIAL[s.material];
  ctx.save();
  ctx.scale(0.62, 1);
  switch (s.shape) {
    case "lid":
      ellipse(ctx, 0, 0, 24, 24);
      paint(ctx, metal(ctx, s.material, -24, -24, 24, 24));
      ellipse(ctx, 0, 0, 16, 16);
      ctx.strokeStyle = shade(c, -0.3);
      ctx.lineWidth = 2;
      ctx.stroke();
      ellipse(ctx, 0, 0, 6, 4);
      paint(ctx, "#2b2b2b", 2);
      break;
    case "round":
      ellipse(ctx, 0, 0, 28, 28);
      paint(ctx, s.material === "wood" ? c : metal(ctx, s.material, -28, -28, 28, 28), 3.5);
      if (s.material === "wood") {
        ctx.strokeStyle = shade(c, -0.3);
        ctx.lineWidth = 2;
        for (const x of [-12, 0, 12]) {
          ctx.beginPath();
          ctx.moveTo(x, -26 + Math.abs(x) * 0.3);
          ctx.lineTo(x, 26 - Math.abs(x) * 0.3);
          ctx.stroke();
        }
      } else {
        ellipse(ctx, 0, 0, 21, 21);
        ctx.strokeStyle = shade(c, -0.35);
        ctx.lineWidth = 2;
        ctx.stroke();
      }
      ellipse(ctx, 0, 0, 8, 8);
      paint(ctx, metal(ctx, "iron", -8, -8, 8, 8), 2.5);
      break;
    case "kite":
      ctx.beginPath();
      ctx.moveTo(-24, -28);
      ctx.quadraticCurveTo(0, -36, 24, -28);
      ctx.quadraticCurveTo(24, 10, 0, 40);
      ctx.quadraticCurveTo(-24, 10, -24, -28);
      ctx.closePath();
      paint(ctx, metal(ctx, s.material, -24, -30, 24, 40), 3.5);
      ctx.beginPath();
      ctx.moveTo(-14, -22);
      ctx.quadraticCurveTo(0, -27, 14, -22);
      ctx.quadraticCurveTo(14, 6, 0, 28);
      ctx.quadraticCurveTo(-14, 6, -14, -22);
      ctx.fillStyle = "#b3261e";
      ctx.fill();
      ctx.fillStyle = "#f5c431";
      ctx.fillRect(-2.5, -20, 5, 38);
      ctx.fillRect(-11, -10, 22, 5);
      break;
    case "tower":
      ctx.beginPath();
      ctx.roundRect(-26, -46, 52, 92, 10);
      paint(ctx, metal(ctx, s.material, -26, -46, 26, 46), 3.5);
      ctx.beginPath();
      ctx.roundRect(-18, -38, 36, 76, 6);
      ctx.strokeStyle = shade(c, -0.35);
      ctx.lineWidth = 2;
      ctx.stroke();
      ellipse(ctx, 0, 0, 10, 13);
      paint(ctx, "#fff6dc", 2);
      for (const [x, y] of [[-20, -40], [20, -40], [-20, 40], [20, 40]] as const) {
        ellipse(ctx, x, y, 2.5, 2.5);
        paint(ctx, INK, 0);
      }
      break;
    case "egg":
      glow(ctx, s.material, time);
      ellipse(ctx, 0, 4, 28, 38);
      paint(ctx, metal(ctx, s.material, -28, -34, 28, 42), 3.5);
      ctx.shadowBlur = 0;
      ellipse(ctx, -8, -12, 6, 10, 0.3);
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      ctx.fill();
      ctx.font = "bold 22px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#a8640c";
      ctx.fillText("♛", 0, 10);
      break;
  }
  ctx.restore();
}

/** Helmet over a head centered at (0,0) of radius r, facing +x. */
export function drawHelmet(ctx: Ctx, h: HelmetItem, r: number, time: number): void {
  const c = MATERIAL[h.material];
  switch (h.shape) {
    case "eggshell": {
      ctx.beginPath();
      ctx.moveTo(-r - 2, -r * 0.2);
      ctx.bezierCurveTo(-r - 2, -r * 1.9, r + 2, -r * 1.9, r + 2, -r * 0.2);
      const n = 7;
      for (let i = 0; i <= n; i++) {
        const x = r + 2 - ((2 * r + 4) * i) / n;
        ctx.lineTo(x, -r * 0.2 + (i % 2 ? -6 : 2));
      }
      ctx.closePath();
      paint(ctx, "#fbf4e6");
      break;
    }
    case "pot": {
      ctx.beginPath();
      ctx.roundRect(-r - 3, -r * 1.35, 2 * r + 6, r * 1.05, 4);
      paint(ctx, metal(ctx, "iron", -r, -r * 1.4, r, -r * 0.3));
      ctx.beginPath();
      ctx.roundRect(-r - 30, -r * 1.1, 28, 6, 3);
      paint(ctx, "#2b2b2b", 2.5);
      ctx.beginPath();
      ctx.roundRect(-r - 5, -r * 0.42, 2 * r + 10, 6, 3);
      paint(ctx, shade(c, -0.15), 2.5);
      break;
    }
    case "viking": {
      for (const side of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(side * r * 0.75, -r * 0.65);
        ctx.quadraticCurveTo(side * (r + 18), -r * 0.7, side * (r + 14), -r * 2.1);
        ctx.quadraticCurveTo(side * (r + 4), -r * 1.15, side * r * 0.4, -r * 0.95);
        ctx.closePath();
        paint(ctx, "#f4ead2");
      }
      ctx.beginPath();
      ctx.arc(0, -r * 0.3, r + 2, Math.PI, 0);
      ctx.closePath();
      paint(ctx, metal(ctx, "iron", 0, -r * 1.4, 0, 0));
      ctx.beginPath();
      ctx.roundRect(-r - 3, -r * 0.42, 2 * r + 6, 6, 3);
      paint(ctx, MATERIAL.bronze, 2.5);
      break;
    }
    case "gladiator": {
      ctx.beginPath();
      ctx.moveTo(-r * 0.6, -r * 1.1);
      for (let i = 0; i < 9; i++) {
        const a = Math.PI + (i / 8) * Math.PI;
        const rr = r + 14 + Math.sin(time * 6 + i) * 1.5;
        ctx.lineTo(Math.cos(a) * rr * 0.9 - 4, -r * 0.7 + Math.sin(a) * rr);
      }
      ctx.lineTo(r * 0.6, -r * 1.1);
      ctx.closePath();
      paint(ctx, "#c62828");
      ctx.beginPath();
      ctx.arc(0, -r * 0.2, r + 2, Math.PI, 0);
      ctx.lineTo(r + 2, r * 0.4);
      ctx.lineTo(r * 0.55, r * 0.4);
      ctx.lineTo(r * 0.55, -r * 0.1);
      ctx.lineTo(-r * 0.2, -r * 0.1);
      ctx.lineTo(-r * 0.2, r * 0.7);
      ctx.lineTo(-r - 2, r * 0.6);
      ctx.closePath();
      paint(ctx, metal(ctx, h.material, 0, -r * 1.3, 0, r));
      break;
    }
    case "knight": {
      ctx.beginPath();
      ctx.moveTo(-r - 3, r * 0.75);
      ctx.lineTo(-r - 3, -r * 0.2);
      ctx.bezierCurveTo(-r - 3, -r * 1.7, r * 0.6, -r * 1.8, r + 4, -r * 0.4);
      ctx.lineTo(r + 4, r * 0.75);
      ctx.closePath();
      paint(ctx, metal(ctx, h.material, 0, -r * 1.5, 0, r));
      ctx.fillStyle = INK;
      ctx.fillRect(0, -r * 0.3, r + 4, 4);
      for (let i = 0; i < 3; i++) {
        ctx.beginPath();
        ctx.arc(r * 0.35 + i * 6, r * 0.25, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case "crown": {
      ctx.save();
      glow(ctx, "gold", time);
      ctx.beginPath();
      ctx.moveTo(-r * 0.85, -r * 0.55);
      ctx.lineTo(-r * 0.95, -r * 1.65);
      ctx.lineTo(-r * 0.45, -r * 1.1);
      ctx.lineTo(0, -r * 1.85);
      ctx.lineTo(r * 0.45, -r * 1.1);
      ctx.lineTo(r * 0.95, -r * 1.65);
      ctx.lineTo(r * 0.85, -r * 0.55);
      ctx.closePath();
      paint(ctx, metal(ctx, "gold", 0, -r * 1.8, 0, -r * 0.5));
      ctx.restore();
      for (const [x, col] of [[-r * 0.45, "#2f6fd6"], [0, "#c62828"], [r * 0.45, "#2e7d32"]] as const) {
        ellipse(ctx, x, -r * 0.8, 3, 3);
        paint(ctx, col, 1.5);
      }
      break;
    }
  }
}

/** Armor over a body ellipse (cx, cy, rx, ry). Caller clips to the body. */
export function drawArmor(ctx: Ctx, a: ArmorItem, cx: number, cy: number, rx: number, ry: number): void {
  const c = MATERIAL[a.material];
  const top = cy - ry * 0.35;
  switch (a.shape) {
    case "sack": {
      ctx.fillStyle = "#c9ab6e";
      ctx.fillRect(cx - rx, top, rx * 2, ry * 2);
      ctx.strokeStyle = "#8a6d3a";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(cx - rx, top + 4);
      ctx.lineTo(cx + rx, top + 4);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#7a3a1a";
      ctx.font = "bold 9px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("FEED", cx + rx * 0.2, cy + ry * 0.45);
      break;
    }
    case "leather": {
      ctx.fillStyle = c;
      ctx.fillRect(cx - rx, top, rx * 2, ry * 2);
      ctx.strokeStyle = shade(c, -0.35);
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.moveTo(cx - rx * 0.6, top - 4);
      ctx.lineTo(cx + rx * 0.7, cy + ry);
      ctx.stroke();
      ctx.fillStyle = MATERIAL.bronze;
      ctx.fillRect(cx - 3, cy + ry * 0.2 - 3, 6, 6);
      break;
    }
    case "chain": {
      ctx.fillStyle = shade(c, -0.1);
      ctx.fillRect(cx - rx, top, rx * 2, ry * 2);
      ctx.strokeStyle = shade(c, -0.45);
      ctx.lineWidth = 1.2;
      for (let y = top + 3; y < cy + ry; y += 5) {
        for (let x = cx - rx + ((y / 5) % 2 ? 2.5 : 0); x < cx + rx; x += 5) {
          ctx.beginPath();
          ctx.arc(x, y, 2.2, 0, Math.PI);
          ctx.stroke();
        }
      }
      break;
    }
    case "plate": {
      ctx.fillStyle = metal(ctx, a.material, cx - rx, top, cx + rx, cy + ry);
      ctx.fillRect(cx - rx, top, rx * 2, ry * 2);
      ctx.strokeStyle = shade(c, -0.4);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(cx - rx, cy + ry * 0.35);
      ctx.quadraticCurveTo(cx, cy + ry * 0.55, cx + rx, cy + ry * 0.35);
      ctx.stroke();
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ellipse(ctx, cx + rx * 0.3, top + ry * 0.35, rx * 0.35, ry * 0.12, -0.2);
      ctx.fill();
      ctx.fillStyle = INK;
      for (const dx of [-0.6, 0, 0.6]) {
        ctx.beginPath();
        ctx.arc(cx + rx * dx, top + 5, 1.6, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case "royal": {
      ctx.fillStyle = "#7a1426";
      ctx.fillRect(cx - rx, top, rx * 2, ry * 2);
      ctx.fillStyle = metal(ctx, "gold", cx, top, cx + rx, cy + ry);
      ctx.fillRect(cx - rx * 0.1, top, rx * 1.2, ry * 2);
      ctx.fillStyle = "#fff";
      for (let x = cx - rx; x < cx + rx; x += 8) {
        ctx.beginPath();
        ctx.arc(x, top + 2, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.fillStyle = INK;
      for (let x = cx - rx + 4; x < cx + rx; x += 16) ctx.fillRect(x, top + 1, 2, 3);
      break;
    }
  }
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - rx, top);
  ctx.lineTo(cx + rx, top);
  ctx.stroke();
}

/** Boots on a shank from (x0,y0) to (x1,y1) (ankle). */
export function drawBoot(ctx: Ctx, b: BootItem, x0: number, y0: number, x1: number, y1: number, time: number): void {
  const c = MATERIAL[b.material];
  const mx = x0 + (x1 - x0) * 0.35;
  const my = y0 + (y1 - y0) * 0.35;
  if (b.shape === "sandals") {
    ctx.strokeStyle = "#7a4a22";
    ctx.lineWidth = 3;
    for (const t of [0.55, 0.8]) {
      const x = x0 + (x1 - x0) * t;
      const y = y0 + (y1 - y0) * t;
      ctx.beginPath();
      ctx.moveTo(x - 5, y - 2);
      ctx.lineTo(x + 5, y + 2);
      ctx.stroke();
    }
    return;
  }
  ctx.save();
  if (b.shape === "winged") glow(ctx, "gold", time);
  ctx.beginPath();
  ctx.moveTo(mx - 5, my);
  ctx.lineTo(mx + 5, my);
  ctx.lineTo(x1 + 5, y1 + 2);
  ctx.lineTo(x1 - 5, y1 + 2);
  ctx.closePath();
  paint(ctx, b.material === "leather" ? c : metal(ctx, b.material, mx - 5, my, mx + 5, my), 2);
  ctx.restore();
  if (b.shape === "winged") {
    ctx.beginPath();
    ctx.moveTo(mx - 4, my + 6);
    ctx.quadraticCurveTo(mx - 20, my - 4, mx - 22, my - 12);
    ctx.quadraticCurveTo(mx - 12, my - 2, mx - 4, my + 2);
    paint(ctx, "#fffbe8", 1.5);
  }
}
